'use client';

import React, { useState, useEffect, useRef, useContext, Suspense } from 'react';
import deepEqual from 'deep-equal';
import { Input } from "@heroui/input";
import { Alert } from "@heroui/alert";
import { Button } from "@heroui/button";
import { SourceObject, MediaObject } from '@/shared/types';
import { getMediaType } from '@/shared/functions';
import { Card, CardBody, CardFooter, CardHeader } from "@heroui/card";
import UnifiedSources from './cells/unifiedSources';
import { HotKeys } from '@/components/hot-keys';
import { FullScreenContext } from '@/app/contexts';
import MediaDisplay from './ui/mediaDisplay';
import { UsableMediaTypes } from '@/shared/constants';
import dynamic from 'next/dynamic';
const MarkdownEditor = dynamic(() => import('./ui/markdownEditor'), {
  ssr: false
});
type FormData = {
  tags?: string[];
  slideTitle?: string;
  slideDescription?: string;
  media?: MediaObject[];
};
interface TitleSlideModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPatchTimeline: (timelinePatch: any) => Promise<void>;
  tags?: string[];
  isLegacy: boolean;
  editingEnabled: boolean;
  timelinePatch?: any;
  mediaSourcesFromChat?: MediaObject[];
  caseId?: string;
}
const hasUnsavedChanges = (formData: FormData | undefined, newFormData: FormData | null) => {
  const same = deepEqual(formData, newFormData);
  if (same) {
    return false;
  }
  return true;
};
const TitleSlideModal: React.FC<TitleSlideModalProps> = ({
  isOpen,
  onClose,
  onPatchTimeline,
  tags,
  isLegacy,
  editingEnabled,
  timelinePatch,
  mediaSourcesFromChat,
  caseId
}) => {
  const [showAlertDialog, setShowAlertDialog] = useState(false);
  const [markdownValue, setMarkdownValue] = useState('');
  const [timelinePatchLocal, setTimelinePatchLocal] = useState<any>(() => timelinePatch ? {
    title: timelinePatch?.title ?? '',
    description: timelinePatch?.description ?? '',
    media: timelinePatch?.media ?? '',
    sources: Array.isArray(timelinePatch?.sources) ? timelinePatch.sources : []
  } : undefined);
  const {
    isFullscreen
  } = useContext(FullScreenContext) || {
    isFullscreen: false
  };
  const titleInputRef = useRef<HTMLInputElement>(null);
  const updateTimeoutRef = useRef<NodeJS.Timeout>();

  // Ref updated synchronously in handleMarkdownChange so blur handler always sees latest
  // (markdownValue lags because it's only set in useEffect after timelinePatchLocal updates)
  const latestDescriptionRef = useRef<string>('');

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);
  useEffect(() => {
    if (!timelinePatch) return;
    const patchToSet = {
      title: timelinePatch?.title || '',
      description: timelinePatch?.description || '',
      media: timelinePatch?.media || '',
      sources: timelinePatch?.sources || []
    };
    setTimelinePatchLocal(patchToSet);
  }, [timelinePatch]);
  useEffect(() => {
    if (timelinePatchLocal != null) {
      const desc = timelinePatchLocal.description ?? '';
      setMarkdownValue(desc);
      latestDescriptionRef.current = desc;
    }
  }, [timelinePatchLocal]);
  const handleTitleChangeFinal = () => {
    if (!editingEnabled || isLegacy) return;
    const descriptionToUse = latestDescriptionRef.current ?? markdownValue;
    setTimelinePatchLocal({
      title: timelinePatchLocal?.title ?? '',
      description: descriptionToUse,
      media: timelinePatchLocal?.media || '',
      sources: timelinePatchLocal?.sources ?? []
    });
  };
  const handleMarkdownChange = (newMarkdown: string) => {
    if (!editingEnabled || isLegacy) return;
    latestDescriptionRef.current = newMarkdown;
    setTimelinePatchLocal({
      title: timelinePatchLocal?.title || '',
      description: newMarkdown,
      media: timelinePatchLocal?.media || ''
    });
  };
  const handleMarkdownChangeFinal = () => {
    if (!editingEnabled || isLegacy) return;
    const descriptionToUse = latestDescriptionRef.current ?? markdownValue;
    setTimelinePatchLocal({
      title: timelinePatchLocal?.title || '',
      description: descriptionToUse,
      media: timelinePatchLocal?.media || ''
    });
  };
  const handleUnifiedSourcesChanges = (sources: SourceObject[], media: MediaObject[]) => {
    if (!editingEnabled || isLegacy) {
      return;
    }
    const descriptionToUse = latestDescriptionRef.current ?? markdownValue;
    setTimelinePatchLocal({
      title: timelinePatchLocal?.title || '',
      description: descriptionToUse,
      media: media ? media[0]?.mediaUrl : '',
      sources: sources || []
    });
  };
  const handleTitleSlideSubmit = (e: React.FormEvent) => {
    if (!editingEnabled || isLegacy) return;
    e.preventDefault();
    const currentTitle = timelinePatchLocal?.title ?? '';
    onPatchTimeline({
      ...timelinePatchLocal,
      title: currentTitle,
      tags
    });
    onClose();
  };
  const handleCancel = () => {
    setTimelinePatchLocal({
      title: timelinePatchLocal?.title || '',
      description: timelinePatchLocal?.description || '',
      media: timelinePatchLocal?.media || ''
    });
    const unsavedChanges = hasUnsavedChanges(timelinePatchLocal, timelinePatch);
    if (unsavedChanges) {
      setShowAlertDialog(true);
    } else {
      setTimelinePatchLocal({
        title: timelinePatch?.title || '',
        description: timelinePatch?.description || '',
        media: timelinePatch?.media || ''
      });
      onClose();
    }
  };
  const handleAlertDialogClose = () => {
    setShowAlertDialog(false);
    setTimelinePatchLocal({
      title: timelinePatch?.title || '',
      description: timelinePatch?.description || '',
      media: timelinePatch?.media || ''
    });
    onClose();
  };
  const handleAlertDialogConfirm = () => {
    setShowAlertDialog(false);
    handleTitleSlideSubmit(new Event('submit') as any);
    onClose();
  };
  return <>
      {!isFullscreen ? <Card className='eventModalCard w-[90vw] sm:min-w-[90vw] sm:min-h-[calc(100vh-120px)]' role="dialog" aria-modal="true" aria-labelledby={`title-slide-modal-title`} aria-describedby={`title-slide-description`} classNames={{
      base: 'box-border flex flex-col absolute top-[60px] sm:top-[85px] left-[20px] sm:left-1/2 sm:transform sm:-translate-x-1/2 sm:mx-4 bg-gray-600 items-center rounded-lg',
      header: 'flex sm:items-center sm:justify-center rounded-t-lg border-b-[1px] border-white/20 border-solid bg-gray-700 m-0 p-1 h-fit',
      body: 'box-border flex items-center overflow-auto w-[90vw] sm:w-[calc(100vw-40px)] h-[calc(100vh-275px)] custom-scrollbar2 px-2 sm:px-0',
      footer: 'flex items-center justify-center rounded-b-lg border-t-[1px] border-white/20 border-solid bg-gray-700 m-0 p-1 h-[70px]'
    }}>
          <HotKeys onCloseModal={handleCancel} isModal={true} />
          <CardHeader id={`title-slide-modal-title`}>
            <div className="flex flex-row items-center justify-start w-full">
              <div className={`flex flex-row items-start sm:items-center gap-4 w-[90%] sm:p-2 sm:pl-4`}>
                <div className="flex flex-row items-center justify-start p-2 sm:p-0 sm:pl-6">
                  <div className="text-white font-light tracking-wider leading-none text-2xl m-0 p-0">Edit Title Slide</div>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <div onSubmit={handleTitleSlideSubmit} id={`title-slide-modal-description`} className="flex flex-col gap-8 items-center text-white font-inter bg-transparent sm:min-w-[300px] w-full sm:w-[calc(70vw)] sm:max-w-fit">
              <div className="flex flex-col gap-2 items-start w-full sm:w-[800px]">
                <label htmlFor="event-title-input" className="text-white/100 font-light tracking-wider text-[14px] m-0 p-0">Title</label>
                <Input ref={titleInputRef} id="event-title-input" disabled={!editingEnabled && !isLegacy} name="slideTitle" variant="flat" label="" labelPlacement="outside" value={timelinePatchLocal?.title ?? ''} onValueChange={value => {
              if (!editingEnabled || isLegacy) return;
              setTimelinePatchLocal({
                title: value,
                description: timelinePatchLocal?.description ?? '',
                media: timelinePatchLocal?.media || '',
                sources: timelinePatchLocal?.sources ?? []
              });
            }} onBlur={editingEnabled && !isLegacy ? handleTitleChangeFinal : () => {}} size="md" radius="sm" classNames={{
              base: 'box-border max-w-[770px] mr-[-17px] w-full',
              inputWrapper: 'box-border'
            }} />
              </div>
              <div className="flex flex-col gap-2 items-start w-full sm:w-[800px]">
                <label htmlFor="event-description-editor" className="text-white/100 font-light tracking-wider text-[14px] m-0 p-0">Description</label>
                <Suspense fallback={<div>Loading...</div>}>
                  <MarkdownEditor id="event-description-editor" markdown={markdownValue} onChange={handleMarkdownChange} onBlurIn={handleMarkdownChangeFinal} debounceMs={300} />
                </Suspense>
              </div>
              <div className="flex flex-col w-full sm:w-[800px]">
                <UnifiedSources caseId={caseId || ''} value={timelinePatchLocal?.sources || []} mediaValue={timelinePatchLocal?.media ? [{
              mediaUrl: timelinePatchLocal?.media,
              mediaType: getMediaType({
                mediaType: 'unknown',
                mediaUrl: timelinePatchLocal?.media
              })
            }] : []} isInModal={true} editingEnabled={editingEnabled && !isLegacy} handleUnifiedSourcesChanges={handleUnifiedSourcesChanges} sourcesFromChat={[]} mediaSourcesFromChat={mediaSourcesFromChat?.filter((mediaItem: MediaObject) => UsableMediaTypes.includes(getMediaType(mediaItem, mediaItem.sourceInfo)))} isTitleSlide={true} />
              </div>
            </div>
          </CardBody>
          <CardFooter>
            {showAlertDialog && <Alert hideIcon={true} classNames={{
          base: 'box-border bg-black text-black/40 absolute bottom-[5px] flex items-start justify-start border-white/20 border-solid border-[1px] shadow-lg w-fit h-fit',
          mainWrapper: 'box-border flex flex-col items-start justify-start m-0 p-0',
          title: 'text-blue-400 font-bold text-lg',
          description: 'text-white italic'
        }}>
                <div className="flex flex-row items-center gap-2">
                  <span className="text-white/100 font-light tracking-wider text-[14px] m-0 p-0 whitespace-nowrap">Unsaved Changes:</span>
                  <Button title="Close" onPress={() => handleAlertDialogClose()} className="bg-white/30 text-black/70 cursor-pointer">
                    Close
                  </Button>
                  <Button title="Save and Close" onPress={() => handleAlertDialogConfirm()} className="bg-white/60 text-white/90 cursor-pointer border-white/80 border-solid border-2">
                    Save and Close
                  </Button>
                </div>
              </Alert>}
            {(!editingEnabled || isLegacy) && <div className="flex flex-row justify-center items-center w-full gap-4 my-1">
                <button className="btn-text secondary bg-white/70 hover:bg-white/90 text-gray-700 hover:text-black" aria-label="Cancel" onClick={handleCancel}>
                  Close
                </button>
              </div>}
            {editingEnabled && !isLegacy && <div className="flex flex-row justify-center items-center w-full gap-4 my-1">
                <button className="btn-text secondary bg-white/70 hover:bg-white/90 text-gray-700 hover:text-black" aria-label="Cancel" onClick={handleCancel}>
                  Cancel
                </button>
                <button aria-label="Save Event" type="submit" className="btn-text bg-green-500 hover:bg-green-600 text-white opacity-100 border-2 border-solid border-white/10 font-bold" onClick={handleTitleSlideSubmit}>
                  Save Changes
                </button>
              </div>}
          </CardFooter>
        </Card> : <MediaDisplay mediaItem={{
      mediaUrl: timelinePatchLocal?.media,
      mediaType: getMediaType({
        mediaType: 'unknown',
        mediaUrl: timelinePatchLocal?.media
      })
    }} isTitleSlide={true} />}
    </>;
};
export default TitleSlideModal;